import React from 'react';

import clsx from 'clsx';
import { FileRejection, useDropzone } from 'react-dropzone';

import { DocumentCategory } from 'enums/DocumentCategory';
import { DocumentStatus } from 'enums/DocumentStatus';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import { getFileExtension } from 'utils/fileHelper';

import styles from './Dropzone.module.scss';
import DropzoneArea from './DropzoneArea';

const DEFAULT_MAX_FILE_SIZE = 10485760;
const INVALID_TYPE_ERROR_CODE = 'file-invalid-type';
const FILE_TOO_LARGE_ERROR_CODE = 'file-too-large';
export const FILE_NUMBER_LIMIT = 5;

export interface DropzoneProps {
  extensions: string[];
  handleFiles: (files: File[], title: string, category: DocumentCategory) => void;
  header: string;
  name: string;
  status: DocumentStatus;
  category: DocumentCategory;
  maxFileSize?: number;
}

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx'];

const getExtensionNotSupportedErrorMessage = (extension: string) => {
  return `File Extension Not Supported: .${extension}`;
};

const Dropzone = ({
  extensions,
  handleFiles,
  name,
  header,
  status,
  category,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
}: DropzoneProps) => {
  const handleDocumentDrop = (accepted: File[], rejected: FileRejection[]) => {
    if (status !== DocumentStatus.Confirmed) {
      if (accepted.length + rejected.length > FILE_NUMBER_LIMIT) {
        return notificationManager.notifyFailure(FailureNotificationType.FileLimitError);
      }

      if (rejected.length) {
        const firstErrorCode = rejected[0].errors[0].code;

        if (firstErrorCode === FILE_TOO_LARGE_ERROR_CODE) {
          return notificationManager.notifyFailure(FailureNotificationType.DocumentIsTooBig);
        }

        if (firstErrorCode === INVALID_TYPE_ERROR_CODE) {
          const fileName = rejected[0].file.name;
          const extension = getFileExtension(fileName);

          return notificationManager.notifyFailure(
            FailureNotificationType.CustomError,
            getExtensionNotSupportedErrorMessage(extension),
          );
        }

        return notificationManager.notifyFailure(FailureNotificationType.DocumentNotUploaded);
      }

      const notAllowedExtensions = accepted
        .filter((file) => !ALLOWED_EXTENSIONS.includes(getFileExtension(file.name.toLowerCase())))
        .map((file) => getFileExtension(file.name.toLowerCase()));

      if (notAllowedExtensions.length > 0) {
        return notificationManager.notifyFailure(
          FailureNotificationType.CustomError,
          getExtensionNotSupportedErrorMessage(notAllowedExtensions[0]),
        );
      }

      handleFiles(accepted, header, category);
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: extensions,
    multiple: true,
    maxSize: maxFileSize,
    noKeyboard: true,
    noClick: status === DocumentStatus.Confirmed,
    onDrop: (accepted, rejected) => {
      handleDocumentDrop(accepted, rejected);
    },
  });

  return (
    <>
      <div {...getRootProps()} className={clsx(styles.dropzone)}>
        <input {...getInputProps()} />
        <DropzoneArea
          {...{ header, name, status, isDragReject }}
          isDragActive={isDragActive && status !== DocumentStatus.Confirmed}
        />
      </div>
    </>
  );
};

export default Dropzone;
